import type { KeyboardEvent, ReactNode } from 'react';

export function TableWrap({
  children,
  busy = false,
  className = '',
  tableClassName = '',
}: {
  children: ReactNode;
  busy?: boolean;
  className?: string;
  /** Extra class(es) on the <table> itself - e.g. 'data-table-sortable' for useSortableTable. */
  tableClassName?: string;
}): JSX.Element {
  return (
    <div className={`table-wrap ${className}`.trim()} aria-busy={busy || undefined}>
      <table className={`data-table ${tableClassName}`.trim()}>{children}</table>
    </div>
  );
}

/** Moves focus between rows with the arrow keys, Home and End. */
function moveRowFocus(event: KeyboardEvent<HTMLTableRowElement>, direction: -1 | 1 | 'first' | 'last'): void {
  const row = event.currentTarget;
  const body = row.parentElement;
  if (!body) return;
  const rows = Array.from(body.querySelectorAll<HTMLTableRowElement>('tr[tabindex]'));
  const index = rows.indexOf(row);
  if (index < 0) return;

  let nextIndex: number;
  if (direction === 'first') nextIndex = 0;
  else if (direction === 'last') nextIndex = rows.length - 1;
  else nextIndex = index + direction;

  const next = rows[nextIndex];
  if (next) {
    event.preventDefault();
    next.focus();
  }
}

/**
 * A table row that behaves like a listbox option: focusable, activated with Enter or Space, and
 * navigable with the arrow keys.
 */
export function SelectableRow({
  selected,
  onSelect,
  children,
  label,
  className = '',
}: {
  selected: boolean;
  onSelect: () => void;
  children: ReactNode;
  /** Announced description of the row, e.g. the file name. */
  label: string;
  className?: string;
}): JSX.Element {
  return (
    <tr
      tabIndex={0}
      className={`row-selectable ${selected ? 'row-selected' : ''} ${className}`.trim()}
      aria-selected={selected}
      aria-label={label}
      onClick={onSelect}
      onKeyDown={(event: KeyboardEvent<HTMLTableRowElement>) => {
        switch (event.key) {
          case 'Enter':
          case ' ':
            event.preventDefault();
            onSelect();
            break;
          case 'ArrowDown':
            moveRowFocus(event, 1);
            break;
          case 'ArrowUp':
            moveRowFocus(event, -1);
            break;
          case 'Home':
            moveRowFocus(event, 'first');
            break;
          case 'End':
            moveRowFocus(event, 'last');
            break;
          default:
            break;
        }
      }}
    >
      {children}
    </tr>
  );
}

/** Row that expands to reveal detail, used by the sync history audit table. */
export function ExpandableRow({
  expanded,
  onToggle,
  summary,
  detail,
  colSpan,
  label,
}: {
  expanded: boolean;
  onToggle: () => void;
  summary: ReactNode;
  detail: ReactNode;
  colSpan: number;
  label: string;
}): JSX.Element {
  return (
    <>
      <tr
        tabIndex={0}
        className={`row-expandable ${expanded ? 'row-expanded' : ''}`}
        aria-expanded={expanded}
        aria-label={label}
        onClick={onToggle}
        onKeyDown={(event: KeyboardEvent<HTMLTableRowElement>) => {
          switch (event.key) {
            case 'Enter':
            case ' ':
              event.preventDefault();
              onToggle();
              break;
            case 'ArrowDown':
              moveRowFocus(event, 1);
              break;
            case 'ArrowUp':
              moveRowFocus(event, -1);
              break;
            default:
              break;
          }
        }}
      >
        {summary}
      </tr>
      {expanded ? (
        <tr className="row-detail">
          <td colSpan={colSpan}>{detail}</td>
        </tr>
      ) : null}
    </>
  );
}
